import { callExternalApi } from "@/services/external-api.service";
import { getAccessTokenSilentlyOutsideComponent } from "@/services/workspace.service";

const API_URL = import.meta.env.VITE_API_SERVER_URL;
const BASE = `${API_URL}/api/users/onboarding-checklist`;

/**
 * Steps in the home checklist. Must match ONBOARDING_STEPS in the API's
 * onboarding-checklist.dto.ts.
 */
export const ONBOARDING_STEPS = {
  WATCH_TOUR: "watch_tour",
  UPLOAD_FILE: "upload_file",
  OPEN_SUMMARY: "open_summary",
  ASK_AI_CHAT: "ask_ai_chat",
  SEARCH: "search",
};

const authorizedConfig = async (url, method, data) => {
  const token = await getAccessTokenSilentlyOutsideComponent();
  const config = {
    url,
    method,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };
  if (data !== undefined) config.data = data;
  return config;
};

/**
 * Fetch the user's checklist progress.
 * @returns {Promise<Object>} { data, error }
 */
export const getOnboardingChecklist = async () => {
  try {
    return await callExternalApi({
      config: await authorizedConfig(BASE, "GET"),
    });
  } catch (error) {
    console.error("Error getting onboarding checklist:", error);
    return {
      data: null,
      error: { message: "Failed to get onboarding checklist" },
    };
  }
};

/**
 * Mark a step complete. Idempotent server-side, so callers do not need to
 * track whether they have already reported the step.
 * @param {string} step one of ONBOARDING_STEPS
 */
export const completeOnboardingStep = async (step) => {
  try {
    return await callExternalApi({
      config: await authorizedConfig(`${BASE}/complete-step`, "POST", { step }),
    });
  } catch (error) {
    console.error("Error completing onboarding step:", error);
    return {
      data: null,
      error: { message: "Failed to complete onboarding step" },
    };
  }
};

/** Permanently hide the checklist for this user. */
export const dismissOnboardingChecklist = async () => {
  try {
    return await callExternalApi({
      config: await authorizedConfig(`${BASE}/dismiss`, "POST"),
    });
  } catch (error) {
    console.error("Error dismissing onboarding checklist:", error);
    return {
      data: null,
      error: { message: "Failed to dismiss onboarding checklist" },
    };
  }
};
